import { ReactNode } from "react";
import * as S from "./styles";
import { DataTableMenu, DataTableMenuItem } from "./DataTableMenu";

import DraggableDiv from "components/DragableDiv";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useLocation } from "react-router-dom";

export interface DataTableHeader<T> {
  field: keyof T;
  label: string;
  type?: "boolean" | "status";
  renderColumn?: (data: T) => ReactNode;
}

interface Props<T> {
  data?: T[];
  headers: DataTableHeader<T>[];
  menuActions?: DataTableMenuItem<T>[];
  isFetching?: boolean;
}

interface DataColumnProps<T> {
  value: T;
  header: DataTableHeader<T>;
}

export const DataTableStatusBadge = S.StatusBadge;

function renderDataColumn<T>({ value, header }: DataColumnProps<T>): ReactNode {
  if (header.renderColumn && header.renderColumn(value) !== undefined) {
    return header.renderColumn(value);
  }

  if (header.type === "status") {
    return (
      <S.StatusBadge variant={value[header.field] ? 'active' : 'deactive'} isActive={value[header.field] as boolean}>
        {value[header.field] === true ? "Ativado" : "Desativado"}
      </S.StatusBadge>
    );
  }

  if (header.type === "boolean")
    return value[header.field] === true ? "Sim" : "Não";

  return <span>{value[header.field] as string}</span>;
}

export function DataTable<T>({
  data,
  headers,
  menuActions,
  isFetching = false,
}: Props<T>) {
  const { pathname } = useLocation();

  if (isFetching) {
    return (
      <S.Wrapper
        isFetching={isFetching}
        data-cy={`${pathname.replace("/", "")}-table`}
      >
        <LoadingSpinner size={26} />
        <span>Buscando dados...</span>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper data-cy={`${pathname.replace("/", "")}-table`}>
      <DraggableDiv scrollVisible>
        <S.Table>
          <S.THead>
            <S.THeadRow>
              {headers.map((header, index) => (
                <S.THeadColumn key={index}>
                  <button>{header.label}</button>
                </S.THeadColumn>
              ))}
            </S.THeadRow>
          </S.THead>
          <S.TableBody>
            {data?.map((value, index) => (
              <S.Row key={index}>
                {headers.map((header, index) => (
                  <S.Column key={index}>{renderDataColumn({ value, header })}</S.Column>
                ))}

                {menuActions && (
                  <DataTableMenu data={value} options={menuActions} />
                )}
              </S.Row>
            ))}
          </S.TableBody>
        </S.Table>
      </DraggableDiv>
    </S.Wrapper>
  );
}
